import SolidPlaneDepartureAltIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureAltIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const TimeLineContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${rem(80)} 1fr ${rem(80)};
  column-gap: ${rem(8)};
  align-items: center;
`

const FlightInfoContainer = styled(Group)`
  width: 100%;
`

const FlightLineContainer = styled(Group)`
  width: 100%;
`

const FlightLine = styled.div`
  width: 100%;
  height: 1px;
  min-height: 1px;
  position: relative;
  flex: 1;
  background-color: ${props => props.theme.palette.neutral.default.two};
`

const FlightLineDots = styled(Group)`
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  height: ${rem(8)};
`

const FlightLineDot = styled.div`
  width: ${rem(8)};
  height: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.default.two};
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  departureTime: string;
  arrivalTime: string;
  /** Purchase-time departure clock time, shown struck through beside the live time when the schedule moved. */
  originalDepartureTime?: string;
  /** Purchase-time arrival clock time, shown struck through beside the live time when the schedule moved. */
  originalArrivalTime?: string;
  arrivalDate?: string;
  departureDate?: string;
  /** Purchase-time departure calendar date, shown struck through beside the live date when the schedule moved. */
  originalDepartureDate?: string;
  /** Purchase-time arrival calendar date, shown struck through beside the live date when the schedule moved. */
  originalArrivalDate?: string;
  departureAirport: string;
  arrivalAirport: string;
  duration?: string;
  stops: number;
}

function FareFamilyFlightTileV2TimeLine(props: Props) {
  const {
    departureAirport,
    departureTime,
    originalDepartureTime,
    arrivalAirport,
    arrivalTime,
    originalArrivalTime,
    duration,
    stops,
    arrivalDate,
    departureDate,
    originalDepartureDate,
    originalArrivalDate,
  } = props

  const stopsArray = useMemo(() => {
    return new Array(stops).fill(undefined)
  }, [stops])

  return (
    <TimeLineContainer>
      <Group direction="vertical">
        <Caption variant="large">{departureAirport}</Caption>
        <Group direction="horizontal" gap="0 4" verticalAlign="center">
          {originalDepartureTime && (
            <Heading variant="heading6" colour="neutral-three">
              <s>{originalDepartureTime}</s>
            </Heading>
          )}
          <Heading variant="heading6" colour={originalDepartureTime ? 'highlight-secondary' : undefined}>
            {departureTime}
          </Heading>
        </Group>
        {(departureDate || originalDepartureDate) && (
          <Group direction="horizontal" gap="0 4" verticalAlign="center">
            {originalDepartureDate && (
              <Caption variant="large" colour="neutral-three">
                <s>{originalDepartureDate}</s>
              </Caption>
            )}
            {departureDate && (
              <Caption variant="large" colour={originalDepartureDate ? 'highlight-secondary' : undefined}>
                {departureDate}
              </Caption>
            )}
          </Group>
        )}
      </Group>

      <Group direction="vertical" horizontalAlign="center">

        <FlightInfoContainer direction="horizontal" gap="0 2" verticalAlign="center">
          <FlightLineContainer direction="vertical" horizontalAlign="center" gap="8 0">
            <CaptionTextBlock variant="medium">{duration}</CaptionTextBlock>

            <FlightLine>
              <FlightLineDots direction="horizontal" gap="0 24">
                {stopsArray.map((_, i) => <FlightLineDot key={i} />)}
              </FlightLineDots>
            </FlightLine>

            {stops > 0 && <CaptionTextBlock variant="medium">
              {pluralizeToString('stop', stops)}
            </CaptionTextBlock>}

            {stops === 0 && <CaptionTextBlock variant="medium" colour="highlight-secondary">
              Direct
            </CaptionTextBlock>}
          </FlightLineContainer>

          <SolidPlaneDepartureAltIcon size="S" />
        </FlightInfoContainer>
      </Group>

      <Group direction="vertical">
        <Caption variant="large">{arrivalAirport}</Caption>
        <Group direction="horizontal" gap="0 4" verticalAlign="center">
          {originalArrivalTime && (
            <Heading variant="heading6" colour="neutral-three">
              <s>{originalArrivalTime}</s>
            </Heading>
          )}
          <Heading variant="heading6" colour={originalArrivalTime ? 'highlight-secondary' : undefined}>
            {arrivalTime}
          </Heading>
        </Group>
        {(arrivalDate || originalArrivalDate) && (
          <Group direction="horizontal" gap="0 4" verticalAlign="center">
            {originalArrivalDate && (
              <Caption variant="large" colour="neutral-three">
                <s>{originalArrivalDate}</s>
              </Caption>
            )}
            {arrivalDate && (
              <Caption variant="large" colour={originalArrivalDate ? 'highlight-secondary' : undefined}>
                {arrivalDate}
              </Caption>
            )}
          </Group>
        )}
      </Group>
    </TimeLineContainer>
  )
}

export default FareFamilyFlightTileV2TimeLine
